/**
 * Device State Matcher Utility
 *
 * Provides strict state matching logic for device lifecycle management.
 * Uses simplified status model with:
 * - deviceStatus: single source of truth for lifecycle state
 * - provisioningStatus: provisioning-specific state (requested/success/failed/deprovisioned)
 * - provisioningType: 'test' | 'live'
 */

import type { LucideIcon } from 'lucide-react'
import { AppLogger } from '@/lib/logger'

const log = AppLogger.getLogger(import.meta)

// Type definitions for status fields
export type DeviceStatus =
  | 'ordered'
  | 'shipped'
  | 'received'
  | 'testing'
  | 'tested'
  | 'available'
  | 'live'
  | 'failed'        // Device failed testing (only reached via a failed test)
  | 'faulty'
  | 'returned'
  | 'retired'
  | 'procured'      // Legacy API status (maps to 'available')
  | 'provisioned'   // Legacy API status (maps to 'live')

export type ProvisioningStatus =
  | 'requested'
  | 'success'
  | 'failed'
  | 'deprovisioned'
  | null
  | undefined

export type ProvisioningType =
  | 'test'
  | 'live'
  | null
  | undefined

// Device data structure for state matching
export interface DeviceDataForMatching {
  device: {
    deviceStatus: DeviceStatus
    deviceNumber: number
  }
  provisioningInfo?: {
    deviceProvisioningStatus?: ProvisioningStatus
    deviceProvisioningType?: ProvisioningType
    deviceProvisioningInfoNumber?: number
  }
}

// State match configuration interface
export interface DeviceStateCondition {
  deviceStatus?: DeviceStatus | DeviceStatus[]
  provisioningStatus?: ProvisioningStatus | ProvisioningStatus[]
  provisioningType?: ProvisioningType | ProvisioningType[]
}

// Action configuration for each state
export interface DeviceStateAction {
  id: string
  label: string
  condition: DeviceStateCondition
  component: React.ComponentType<any>
  icon: LucideIcon
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link'
  description?: string
}

/**
 * Normalizes legacy device statuses to current statuses
 * @param status - The device status to normalize
 * @returns The normalized device status
 */
export function normalizeDeviceStatus(status: DeviceStatus): DeviceStatus {
  // Map legacy API statuses to new statuses
  if (status === 'procured') return 'available'
  if (status === 'provisioned') return 'live'
  return status
}

/**
 * Checks if a value matches the allowed values (single or array)
 * @param value - The value to check
 * @param allowed - Single value or array of allowed values
 * @returns True if the value matches
 */
function matchesValue<T>(value: T, allowed: T | T[]): boolean {
  const allowedArray = Array.isArray(allowed) ? allowed : [allowed]
  return allowedArray.includes(value)
}

/**
 * Strictly matches device state against a condition
 *
 * @param deviceData - The device data to check
 * @param condition - The condition to match against
 * @returns True if all condition fields match the device state
 */
export function matchesState(
  deviceData: DeviceDataForMatching,
  condition: DeviceStateCondition
): boolean {
  // Extract status values with null-safe handling
  const deviceStatus = normalizeDeviceStatus(deviceData.device.deviceStatus)
  const provisioningStatus = deviceData.provisioningInfo?.deviceProvisioningStatus
  const provisioningType = deviceData.provisioningInfo?.deviceProvisioningType

  log.debug('Checking state match:', {
    deviceStatus,
    provisioningStatus,
    provisioningType,
    condition,
    deviceData: {
      device: deviceData.device,
      provisioningInfo: deviceData.provisioningInfo
    }
  });

  // Match deviceStatus
  if (condition.deviceStatus !== undefined) {
    if (!matchesValue(deviceStatus, condition.deviceStatus)) {
      log.debug('Failed on deviceStatus:', { actual: deviceStatus, expected: condition.deviceStatus });
      return false
    }
  }

  // Match provisioningStatus (handle null/undefined explicitly)
  if (condition.provisioningStatus !== undefined) {
    if (!matchesValue(provisioningStatus as ProvisioningStatus, condition.provisioningStatus)) {
      log.debug('Failed on provisioningStatus:', { actual: provisioningStatus, expected: condition.provisioningStatus });
      return false
    }
  }

  // Match provisioningType (handle null/undefined explicitly)
  if (condition.provisioningType !== undefined) {
    if (!matchesValue(provisioningType as ProvisioningType, condition.provisioningType)) {
      log.debug('Failed on provisioningType:', { actual: provisioningType, expected: condition.provisioningType });
      return false
    }
  }

  log.debug('State match SUCCESS!');
  return true
}

/**
 * Finds the first action that matches the device state
 *
 * @param deviceData - The device data to check
 * @param actions - Array of actions to check against
 * @returns The first matching action, or null if none match
 */
export function getAvailableAction(
  deviceData: DeviceDataForMatching,
  actions: DeviceStateAction[]
): DeviceStateAction | null {
  return actions.find(action => matchesState(deviceData, action.condition)) || null
}

/**
 * Finds all actions that match the device state
 *
 * @param deviceData - The device data to check
 * @param actions - Array of actions to check against
 * @returns Array of all matching actions
 */
export function getAllAvailableActions(
  deviceData: DeviceDataForMatching,
  actions: DeviceStateAction[]
): DeviceStateAction[] {
  return actions.filter(action => matchesState(deviceData, action.condition))
}
